import { Link } from 'react-router-dom';
import { Globe, Moon, Sun, ChevronDown, ArrowRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import ZitiusLogo from '../brand/ZitiusLogo';

const PublicSiteHeader = ({ onDarkHero = false }) => {
  const { t, language, setLanguage } = useLanguage();
  const { mode, toggleMode } = useTheme();
  const { isAuthenticated } = useAuth();

  const chromeClass = onDarkHero
    ? 'border-white/15 bg-black/20 text-white backdrop-blur-xl'
    : 'border-border bg-card/90 text-foreground backdrop-blur-xl';

  const ghostClass = onDarkHero
    ? 'text-white hover:bg-white/10 hover:text-white'
    : 'text-foreground hover:bg-accent hover:text-accent-foreground';

  const outlineClass = onDarkHero
    ? 'border-white/20 bg-white/10 text-white hover:bg-white/20 hover:text-white'
    : 'border-border bg-background/80 text-foreground hover:bg-accent hover:text-accent-foreground';

  return (
    <header className='sticky top-0 z-40 px-4 pt-4 sm:px-6'>
      <div
        className={`mx-auto flex max-w-7xl items-center justify-between rounded-full border px-4 py-3 shadow-[0_18px_60px_rgba(5,15,35,0.18)] ${chromeClass}`}
      >
        <Link to='/' className='shrink-0'>
          <ZitiusLogo size='sm' variant={onDarkHero ? 'light' : 'default'} />
        </Link>

        <nav className='hidden items-center gap-1 lg:flex'>
          <Button asChild variant='ghost' className={ghostClass}>
            <a href='/#features'>{t('marketing.nav.features')}</a>
          </Button>
          <Button asChild variant='ghost' className={ghostClass}>
            <a href='/#method'>{t('marketing.nav.method')}</a>
          </Button>
          <Button asChild variant='ghost' className={ghostClass}>
            <a href='/#community'>{t('marketing.nav.community')}</a>
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='ghost' className={ghostClass}>
                {t('marketing.nav.explore')}
                <ChevronDown className='ml-2 h-4 w-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' className='w-64'>
              <DropdownMenuItem asChild>
                <Link to='/features'>{t('marketing.nav.platformOverview')}</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a href='/#features'>{t('marketing.nav.featureHighlights')}</a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a href='/#community'>{t('marketing.nav.communityStories')}</a>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to='/login2'>{t('marketing.nav.loginMenu')}</Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        <div className='flex items-center gap-2'>
          <Button
            variant='ghost'
            size='icon'
            onClick={() => setLanguage(language === 'en' ? 'no' : 'en')}
            title={t('nav.language')}
            className={ghostClass}
          >
            <Globe className='h-4 w-4' />
          </Button>

          <Button
            variant='ghost'
            size='icon'
            onClick={toggleMode}
            title={mode === 'dark' ? t('nav.lightMode') : t('nav.darkMode')}
            className={ghostClass}
          >
            {mode === 'dark' ? <Sun className='h-4 w-4' /> : <Moon className='h-4 w-4' />}
          </Button>

          {isAuthenticated ? (
            <Button asChild variant='outline' className={`hidden sm:inline-flex ${outlineClass}`}>
              <Link to='/dashboard'>{t('marketing.cta.openDashboard')}</Link>
            </Button>
          ) : null}

          <Button asChild className='rounded-full bg-[hsl(var(--brand-electric))] text-white shadow-lg hover:bg-[hsl(var(--brand-electric-strong))]'>
            <Link to='/login2'>
              {t('auth.login')}
              <ArrowRight className='ml-2 h-4 w-4' />
            </Link>
          </Button>
        </div>
      </div>
    </header>
  );
};

export default PublicSiteHeader;
